// Recovery for stale clients after a deploy.
//
// A tab that is still running an older index.html holds hashed chunk URLs that
// the CDN no longer serves. The next lazy route the user opens fails its dynamic
// import() and, with no route-level boundary on public pages, white-screens them
// on the top-level ErrorBoundary. KAN-546 / KAN-548.
//
// Shared by lazyWithChunkReload in App.tsx and the vite:preloadError listener in
// main.tsx — Vite's preload helper dispatches that event and then rethrows the
// same error into the import() promise, so both paths see a single failure and
// must contend for one reload through claimChunkReload().

const CHUNK_RELOAD_KEY = "homehelpy_chunk_reload";

// A reload loop re-fails within ~1s, so a short cooldown is enough to break it.
// A genuine second deploy while the tab is still open lands well outside it.
const RELOAD_COOLDOWN_MS = 60_000;
let chunkReloadInFlight = false;

// Each browser engine words a failed dynamic import() differently — KAN-546.
const CHUNK_ERROR_MESSAGE_PATTERNS = [
  "Failed to fetch dynamically imported module", // Chrome/Edge
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari/WebKit
  "Unable to preload CSS", // Vite's CSS preload helper
];

const MAX_CAUSE_DEPTH = 5;

/**
 * True when a resolved dynamic import is actually a module namespace React.lazy can use.
 *
 * A failed `import()` does not always reject. Cancelling Vite's `vite:preloadError`
 * event stops Vite rethrowing, and the import promise then **resolves with `undefined`**
 * instead. A `.catch()` never sees that, so the bad value reaches React.lazy, which does
 * `"default" in mod` and throws `TypeError: undefined is not an Object` from inside the
 * render — surfacing as an error boundary fallback rather than a caught import failure.
 *
 * Every lazy factory in the app has to guard its resolved value, not just its rejection.
 */
export function isLazyModule(mod: unknown): boolean {
  return !!mod && typeof mod === "object" && "default" in mod;
}

/**
 * Deliberately does NOT gate on `err instanceof TypeError`. Errors that wrap the
 * original (err.cause) and errors from another realm — an iOS in-app webview,
 * for instance — fail that check even when `name === "TypeError"`, which is how
 * KAN-555 slipped past both the retry and the reload: the /faq chunk 404'd and
 * the boundary caught it 104ms later, with none of the backoff delays
 * importWithRetry would have applied.
 */
export function isChunkLoadError(err: unknown): boolean {
  let cursor: unknown = err;

  for (let depth = 0; cursor && depth < MAX_CAUSE_DEPTH; depth++) {
    const { message } = cursor as { message?: unknown };
    if (
      typeof message === "string" &&
      CHUNK_ERROR_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
    ) {
      return true;
    }
    cursor = (cursor as { cause?: unknown }).cause;
  }

  return false;
}

/**
 * Reserves the right to reload, returning false if one was already claimed
 * within the cooldown. Previously a one-shot sessionStorage flag that nothing
 * ever cleared, which left any tab that recovered once permanently unprotected
 * against the next deploy.
 */
export function claimChunkReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage is unavailable in some restricted contexts. Reloading
    // without loop protection still beats white-screening the user.
  }
  chunkReloadInFlight = true;
  return true;
}

export function isChunkReloadInFlight(): boolean {
  return chunkReloadInFlight;
}

export function suspendForChunkReload(): Promise<never> {
  return new Promise<never>(() => {});
}

/**
 * Drops the workbox precache before reloading. This remains the fallback for clients
 * where service-worker registration is unavailable or a stale CDN shell references a
 * chunk that no longer exists.
 *
 * Never resolves — callers await it to keep React suspended on the fallback
 * rather than surfacing the error while the document tears down.
 */
export async function reloadForNewDeploy(): Promise<never> {
  chunkReloadInFlight = true;

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter((key) => key.includes("precache")).map((key) => caches.delete(key)),
      );
    }
  } catch {
    // Best-effort — reload regardless.
  }

  window.location.reload();
  return new Promise<never>(() => {});
}
